import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';

import { LanguageService } from '@fcom/ui-translate';
import { loginStatus, profileName, profileType } from '@fcom/core/selectors';
import { LoginStatus, ProfileType } from '@fcom/core-api/login';
import { CmsPageService, DeepLinkSource, isNavigationEvent, NavigationMenuService } from '@fcom/common';
import { Breadcrumb } from '@fcom/ui-components';
import { LoginDialogComponent, LoginWidgetContext } from '@fcom/common/login';
import { RootPaths } from '@fcom/core/constants';
import { unsubscribe } from '@fcom/core/utils';

import { getDeepLinkSource, getPassengers, getStage } from '../../store/selectors';
import { StatusActions } from '../../store';
import { Stage, State } from '../../interfaces';
import { BREADCRUMBS } from '../../constants';
import { Passenger, Passengers } from '../../api/models';

function showStatusBar(currentStage) {
  return ![Stage.LOGIN, Stage.LOADING, Stage.SUCCESS, Stage.ERROR, Stage.PROCESSING].includes(currentStage);
}

@Component({
  selector: 'fin-upgrade-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OneUpgradeLayoutComponent implements OnInit, OnDestroy {
  readonly LoginStatus = LoginStatus;
  readonly Stage = Stage;
  readonly LoginWidgetContext = LoginWidgetContext;
  private subscriptions: Subscription = new Subscription();

  loginStatus$: Observable<LoginStatus>;
  isNavigationMenuOpen$: Observable<boolean>;
  currentStage$: Observable<{ stage: Stage }>;
  profileName$: Observable<string>;
  profileType$: Observable<ProfileType>;
  passengers$: Observable<Passenger[]>;
  breadcrumbs$: Observable<Breadcrumb[]>;

  homeUrl: string;
  showBack = false;
  showStatusBar = false;
  currentStage: Stage;
  showBookingHeader = false;
  addWrappingStyles = false;

  @ViewChild('loginDialog', { static: false }) loginDialog: LoginDialogComponent;
  constructor(
    private languageService: LanguageService,
    private store$: Store<State>,
    private navigationMenuService: NavigationMenuService,
    private pageService: CmsPageService,
    private router: Router
  ) {
    this.isNavigationMenuOpen$ = navigationMenuService.isNavigationMenuOpen$;

    this.subscriptions.add(
      this.router.events
        .pipe(
          filter(isNavigationEvent),
          switchMap(() => {
            const pathRelativeToUpgradeRoot = this.router.url.split(RootPaths.ONE_UPGRADE_ROOT)[1] || '';

            if (!pathRelativeToUpgradeRoot) {
              return of(false);
            }

            return this.pageService.getPageDataAndSetMeta(`${RootPaths.ONE_UPGRADE_ROOT}${pathRelativeToUpgradeRoot}`);
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus());
    this.profileName$ = this.store$.pipe(profileName());
    this.profileType$ = this.store$.pipe(profileType());
    this.passengers$ = this.store$.pipe(select(getPassengers));

    this.currentStage$ = combineLatest([
      this.store$.pipe(select(getStage)),
      this.store$.pipe(select(getDeepLinkSource)),
      this.store$.pipe(select(getPassengers)),
    ]).pipe(
      tap(([stage, source, passengers]) => {
        this.showStatusBar = showStatusBar(stage);
        this.currentStage = stage;
        this.showBookingHeader = ![Stage.LOGIN, Stage.LOADING, Stage.ERROR].includes(stage);
        this.addWrappingStyles = ![Stage.LOGIN, Stage.LOADING].includes(stage);
        this.showBack = this.shouldShowBackButton(stage, source, passengers);
      }),
      map(([currentStage]) => ({ stage: currentStage }))
    );
    this.breadcrumbs$ = combineLatest([
      this.currentStage$,
      this.passengers$,
      this.languageService.translate('oneUpgrade.breadcrumbs'),
    ]).pipe(
      map(([currentStage, passengers, breadcrumbs]) => {
        return this.formatBreadcrumbs(currentStage.stage, breadcrumbs, passengers);
      })
    );
    this.homeUrl = this.languageService.langValue ? `/${this.languageService.langValue}` : '/en';
  }

  toggleMenu(): void {
    this.navigationMenuService.toggleMenuOpen();
  }

  navigateToStep(step: Breadcrumb): void {
    this.store$.dispatch(StatusActions.setStage({ stage: step.id as Stage }));
  }

  formatBreadcrumbs(currentStage: Stage, breadcrumbs: { [x: string]: string }, passengers: Passenger[]): Breadcrumb[] {
    return BREADCRUMBS.map((step) => {
      if (step.id === Stage.PASSENGERS && passengers.length === 1) {
        return;
      }

      return {
        id: step.id,
        name: breadcrumbs[step.translationKey],
        active: step.id === currentStage,
        disabled: currentStage === Stage.SUCCESS || currentStage === Stage.PROCESSING,
      };
    }).filter((x) => !!x);
  }

  onHomeClick(url: string): void {
    this.router.navigateByUrl(url);
  }

  onBackClick(): void {
    this.subscriptions.add(
      combineLatest([this.breadcrumbs$, this.passengers$])
        .pipe(take(1))
        .subscribe(([breadcrumbs, passengers]) => {
          const currentBreadcrumb = breadcrumbs.find((breadcrumb) => breadcrumb.active);

          let toStage = Stage.LOGIN;

          if (currentBreadcrumb.id === Stage.REVIEW) {
            toStage = Stage.FLIGHTS;
          }

          if (currentBreadcrumb.id === Stage.FLIGHTS) {
            toStage = passengers.length === 1 ? Stage.LOGIN : Stage.PASSENGERS;
          }

          this.store$.dispatch(StatusActions.setStage({ stage: toStage }));
        })
    );
  }

  shouldShowBackButton(stage: Stage, source: DeepLinkSource, passengers: Passengers): boolean {
    if (source !== DeepLinkSource.FCOM) {
      return stage !== Stage.SUCCESS;
    }

    if (stage === Stage.PASSENGERS || stage === Stage.SUCCESS) {
      return false;
    }

    if (stage === Stage.REVIEW) {
      return true;
    }

    return passengers.length < 2 ? false : true;
  }
}
